import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import useOrganizationService from '../../../app/services/hooks/useOrganizationService';
import ButtonGeneral from '../../../components/buttons/ButtonGeneral';
import logo_monere from '../../../assets/images/logo_monere.png';
import '../../Auth/Login/Login.css';

interface ClientInvitationData {
    organization_name: string;
    organization_type: string;
    client_email: string;
    added_by: string | null;
    branding: { name: string; logo_url: string | null; primary_color: string };
}

/**
 * Invitación de un estudio a su cartera, para quien ya tenía cuenta en Monere:
 * la persona decide si acepta que el estudio gestione sus trámites.
 */
const ClientInvitation: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const { getClientInvitation, respondClientInvitation, fetchingClientInvitation } = useOrganizationService();
    const [invitation, setInvitation] = useState<ClientInvitationData | null>(null);
    const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);
    const [responding, setResponding] = useState<'accept' | 'decline' | null>(null);

    useEffect(() => {
        getClientInvitation(token, {
            onSuccess: (response) => setInvitation(response.data),
            onError: (response) => setResult({ ok: false, message: response.message })
        });
    }, [token]);

    const respond = (action: 'accept' | 'decline') => {
        setResponding(action);
        respondClientInvitation(token, action, {
            onSuccess: (response) => {
                setResponding(null);
                setResult({ ok: action === 'accept', message: response.message });
            },
            onError: (response) => {
                setResponding(null);
                setResult({ ok: false, message: response.message });
            }
        });
    };

    const accent = invitation?.branding?.primary_color || '#001849';

    return (
        <div className="login-container">
            <div className="login-background"><div className="login-background-pattern" /></div>
            <div className="container position-relative" style={{ maxWidth: 560 }}>
                <div className="card shadow">
                    <div className="card-body p-4 p-md-5 text-center">
                        {invitation?.branding?.logo_url ? (
                            <img src={invitation.branding.logo_url} alt={invitation.branding.name} style={{ maxHeight: 64, maxWidth: 240 }} className="mb-4" />
                        ) : (
                            <img src={logo_monere} alt="Monere" style={{ height: 56 }} className="mb-4" />
                        )}

                        {fetchingClientInvitation && !invitation && !result && <div className="spinner-border text-primary" />}

                        {result && (
                            <div data-testid="client-invitation-result">
                                <i className={`fas ${result.ok ? 'fa-check-circle text-success' : 'fa-info-circle text-secondary'} fa-3x mb-3`} />
                                <p className="mb-3">{result.message}</p>
                                <a href="/login" className="btn btn-outline-primary btn-sm">Ir a iniciar sesión</a>
                            </div>
                        )}

                        {invitation && !result && (
                            <>
                                <h3 className="mb-2" style={{ color: accent }}>{invitation.organization_name}</h3>
                                <p className="text-muted">
                                    {invitation.added_by ? `${invitation.added_by} te invitó` : 'Te invitaron'} a su cartera de clientes.
                                </p>
                                <div className="bg-light rounded p-3 mb-4 small text-start">
                                    Si aceptas, <strong>{invitation.organization_name}</strong> podrá gestionar en Monere los
                                    trámites que inicies desde tu cuenta ({invitation.client_email}) y verá tus datos de contacto.
                                    Si rechazas, tu cuenta sigue igual y el estudio no verá lo que inicies por tu cuenta; los
                                    trámites que el estudio ya lleva a tu nombre siguen con el estudio.
                                </div>
                                <div className="d-flex gap-2 justify-content-center">
                                    <ButtonGeneral onClick={() => respond('decline')} title="Rechazar" variant="outline"
                                                   loading={responding === 'decline'} disabled={!!responding} />
                                    <ButtonGeneral onClick={() => respond('accept')} title="Aceptar invitación" icon="fa-check"
                                                   loading={responding === 'accept'} disabled={!!responding} />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClientInvitation;
